import { VueRenderer } from '@tiptap/vue-2';
import tippy, { sticky } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import MentionSuggestionRichText from 'vcomponents/richtext/customoptions/MentionSuggestionRichText.vue';
import { capitalize } from 'js/vue/filters/text';

let currentPopup = null;

export default {
    /**
     * Returns a list of staff members that match the given query.
     * Used on the MentionSuggestionRichText component.
     */
    async items({ query }) {
        const payload = { blocked: false, limit: 10 };
        if (query) payload.q = query;

        try {
            const result = await ApiClient.get('/staffs/autocomplete', payload);
            return result.data.data
                .filter((staff) => staff.object.id !== window.sellsyapp.$store.state.user.id)
                .map((staff) => {
                    return {
                        name: `${staff.first_name} ${staff.last_name}`,
                        id: staff.object.id,
                        firstname: capitalize(staff.first_name),
                        lastname: capitalize(staff.last_name),
                        email: staff.email,
                        avatar: staff.avatar ?? null,
                    };
                });
        } catch (error) {
            console.error(error);
        }
    },

    /**
     * Renders the CustomSuggestion component.
     */
    render: () => {
        let component = null;
        let popup = null;

        const sugestionLoading = (boolean) => {
            const mentionSuggestionComponent = component?.ref?.$refs.suggestions;
            mentionSuggestionComponent?.__vue__.setLoading(boolean);
        };

        // Destroy the current popup
        const destroyCurrentPopup = () => {
            if (currentPopup) {
                currentPopup.destroy();
                currentPopup = null;
            }
        };

        const placementUpdate = {
            fn() {
                return {
                    onMount(instance) {
                        const parentRect = instance.reference.getBoundingClientRect();
                        const childRect = instance.popper.getBoundingClientRect();
                        if (childRect.left < parentRect.left) {
                            instance.setProps({ placement: 'bottom-start' });
                        } else if (childRect.right > parentRect.right) {
                            +instance.setProps({ placement: 'bottom-end' });
                        }
                    },
                };
            },
        };

        // Destroy the popup when clicking outside of it
        const destroyPopupOnClickOutside = (event) => {
            if (currentPopup) {
                destroyCurrentPopup();
            }
            event.stopPropagation();
        };
        document.addEventListener('click', destroyPopupOnClickOutside);

        return {
            /**
             * Initializes the CustomSuggestion component.
             */
            onStart(props) {
                component = new VueRenderer(MentionSuggestionRichText, {
                    propsData: props,
                    editor: props.editor,
                });

                if (!props.clientRect) {
                    return;
                }

                destroyCurrentPopup();

                const mentionSuggestionComponent = component?.ref?.$refs.suggestions;
                popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    sticky: false,
                    plugins: [sticky, placementUpdate],
                    content: mentionSuggestionComponent,
                    trigger: 'click manual',
                    arrow: false,
                    placement: 'bottom-start',
                    showOnCreate: true,
                    interactive: true,
                    theme: 'mention',
                    inertia: true,
                    duration: [400, 200],
                });
                currentPopup = popup[0];
                sugestionLoading(false);
            },

            /**
             * Updates the CustomSuggestion component.
             */
            onBeforeUpdate() {
                sugestionLoading(true);
            },

            /**
             * Updates the CustomSuggestion component.
             */
            onUpdate(props) {
                sugestionLoading(false);

                component.updateProps(props);

                if (!props.clientRect) {
                    return;
                }

                popup[0].setProps({
                    getReferenceClientRect: props.clientRect,
                });
            },

            /**
             * Handles keydown events for the CustomSuggestion component.
             */
            onKeyDown(props) {
                if (props.event.key === 'Escape') {
                    popup[0].hide();

                    return true;
                }

                return component?.ref?.onKeyDown(props);
            },

            /**
             * Cleans up the CustomSuggestion component.
             */
            onExit() {
                popup[0].destroy();
                component.destroy();
            },
        };
    },
};
